import { CostCategory, Customer, Material } from '~/types';
import {
    AdministrativeFormValues,
    SaveAdministrativeDataPayload,
    WorkOrderDetail,
    WorkOrderStatuses,
} from '~/workorder/types';

export const EDIT_WORK_ORDER_ADMIN_FORM = 'editWorkOrderAdministrative';

export const EDIT_WORK_ORDER_ADMIN_FORM_FIELDS = [
    'personnelId',
    'repairLocation',
    'workType',
    'costCategory',
    'chargedDepartmentId',
    'customer',
    'project',
    'sortingType',
    'invoiceAcceptanceUserId',
] as const;

/** Close-issue–aligned field names for `useForm` / `Select` components */
export interface EditWorkOrderAdministrativeFormValues {
    personnelId: number;
    repairLocation: string;
    workType: number;
    costCategory: number;
    chargedDepartmentId: number;
    customer: number;
    project: number;
    sortingType: number;
    invoiceAcceptanceUserId: number;
}

export const administrativeToEditFormValues = (
    values: AdministrativeFormValues,
): EditWorkOrderAdministrativeFormValues => ({
    personnelId: Number(values.personnelId ?? 0),
    repairLocation: String(values.repairLocation ?? ''),
    workType: Number(values.workType ?? 0),
    costCategory: Number(values.costCategoryId ?? 0),
    chargedDepartmentId: Number(values.chargedDepartmentId ?? 0),
    customer: Number(values.customerId ?? 0),
    project: Number(values.projectId ?? 0),
    sortingType: Number(values.sortingType ?? 0),
    invoiceAcceptanceUserId: Number(values.invoiceAcceptanceUserId ?? 0),
});

export const editFormValuesToAdministrative = (
    values: EditWorkOrderAdministrativeFormValues,
): AdministrativeFormValues => ({
    personnelId: Number(values.personnelId ?? 0),
    repairLocation: String(values.repairLocation ?? ''),
    workType: Number(values.workType ?? 0),
    costCategoryId: Number(values.costCategory) > 0 ? Number(values.costCategory) : null,
    chargedDepartmentId: Number(values.chargedDepartmentId) > 0 ? Number(values.chargedDepartmentId) : null,
    customerId: Number(values.customer) > 0 ? Number(values.customer) : null,
    projectId: Number(values.project) > 0 ? Number(values.project) : null,
    sortingType: Number(values.sortingType),
    invoiceAcceptanceUserId: Number(values.invoiceAcceptanceUserId) > 0
        ? Number(values.invoiceAcceptanceUserId)
        : null,
});

export const administrativeCascadeToFormPatch = (
    admin: AdministrativeFormValues,
): Partial<EditWorkOrderAdministrativeFormValues> => ({
    costCategory: Number(admin.costCategoryId ?? 0),
    chargedDepartmentId: Number(admin.chargedDepartmentId ?? 0),
    customer: Number(admin.customerId ?? 0),
    project: Number(admin.projectId ?? 0),
    sortingType: Number(admin.sortingType ?? 0),
    invoiceAcceptanceUserId: Number(admin.invoiceAcceptanceUserId ?? 0),
});

export const detailToEditFormValues = (detail: WorkOrderDetail): EditWorkOrderAdministrativeFormValues => (
    administrativeToEditFormValues(detailToAdministrativeFormValues(detail))
);

export const isWorkOrderSelectValueSet = (value: number | null | undefined): boolean => (
    value !== null && value !== undefined && Number(value) !== 0
);

export const isWorkOrderSortingTypeSet = (value: string | number | null | undefined): boolean => (
    value !== null && value !== undefined && String(value).trim() !== ''
);

export const canEditResponsiblePersonnel = (status: number): boolean => (
    status !== WorkOrderStatuses.APPROVED && status !== WorkOrderStatuses.FINISHED
);

export const canEditMmFields = (status: number): boolean => (
    status !== WorkOrderStatuses.APPROVED
);

export const shouldPromptWorkTypeChangeOnSelect = (
    detail: WorkOrderDetail,
    newWorkType: number,
): boolean => {
    if (!canEditMmFields(detail.status)) {
        return false;
    }
    const originalWorkType = Number(detail.workType ?? 0);
    if (Number(newWorkType) === originalWorkType) {
        return false;
    }
    return (detail.summary?.totalHours ?? 0) > 0 || (detail.summary?.totalMaterials ?? 0) > 0 || (detail.summary?.totalParts ?? 0) > 0;
};

export const hasMaterialsForCostCategory = (
    materials: Material[],
    costCategoryId: number | null | undefined,
): boolean => {
    const categoryId = Number(costCategoryId ?? 0);
    if (categoryId <= 0) {
        return false;
    }
    return materials.some(
        m => !m.deleted && Number(m.categoryId) === categoryId && m.issueId === null,
    );
};

export const detailToAdministrativeFormValues = (detail: WorkOrderDetail): AdministrativeFormValues => ({
    personnelId: Number(detail.personnel?.id ?? 0),
    repairLocation: String(detail.repairLocation ?? '').trim(),
    workType: Number(detail.workType ?? 0),
    costCategoryId: detail.costCategoryId,
    chargedDepartmentId: detail.chargedDepartmentId,
    customerId: detail.customerId,
    projectId: detail.projectId,
    sortingType: Number(detail.sortingType),
    invoiceAcceptanceUserId: detail.invoiceAcceptanceUserId,
});

export const normalizeRepairLocation = (value: string): string => (
    String(value ?? '').trim()
);

export const findCostCategory = (
    costCategories: CostCategory[],
    costCategoryId: number | null | undefined,
): CostCategory | undefined => costCategories.find(
    ({ id }) => Number(id) === Number(costCategoryId),
);

export const findCustomer = (
    customers: Customer[],
    customerId: number | null | undefined,
): Customer | undefined => customers.find(
    ({ id }) => Number(id) === Number(customerId),
);

export const shouldShowChargedDepartment = (category: CostCategory | undefined): boolean => (
    !!category?.chargeDepartment
);

export const shouldShowCustomer = (category: CostCategory | undefined): boolean => (
    !!category?.displayCustomer
);

export const shouldShowProject = (customer: Customer | undefined): boolean => (
    !!customer?.displayProject
);

export const shouldShowSorting = (customer: Customer | undefined): boolean => (
    !!customer?.needsSorting
);

export const shouldShowInvoiceAcceptance = (customer: Customer | undefined): boolean => (
    !!customer?.isInvoiceAcceptance
);

export const shouldRequireInvoiceApprover = (customer: Customer | undefined): boolean => (
    !!customer?.isInvoiceAcceptanceRequired
);

export const applyCostCategoryCascade = (
    prev: AdministrativeFormValues,
    newCategoryId: number | null,
    costCategories: CostCategory[],
    customers: Customer[],
    initial: AdministrativeFormValues,
): AdministrativeFormValues => {
    const category = findCostCategory(costCategories, newCategoryId);
    let next: AdministrativeFormValues = {
        ...prev,
        costCategoryId: newCategoryId,
    };

    if (!shouldShowChargedDepartment(category)) {
        next.chargedDepartmentId = null;
    } else if (!isWorkOrderSelectValueSet(prev.chargedDepartmentId)) {
        next.chargedDepartmentId = initial.chargedDepartmentId;
    }

    if (!shouldShowCustomer(category)) {
        next.customerId = null;
        next.projectId = null;
        next.sortingType = null;
        next.invoiceAcceptanceUserId = null;
        return next;
    }

    if (!isWorkOrderSelectValueSet(prev.customerId)) {
        next.customerId = initial.customerId;
    }

    return applyCustomerCascade(next, next.customerId, initial, costCategories, customers);
};

export const applyCustomerCascade = (
    prev: AdministrativeFormValues,
    newCustomerId: number | null,
    initial: AdministrativeFormValues,
    costCategories: CostCategory[],
    customers: Customer[],
): AdministrativeFormValues => {
    const category = findCostCategory(costCategories, prev.costCategoryId);
    const next: AdministrativeFormValues = {
        ...prev,
        customerId: newCustomerId,
    };

    if (!shouldShowCustomer(category)) {
        return next;
    }

    const customer = findCustomer(customers, newCustomerId);

    if (!shouldShowProject(customer)) {
        next.projectId = null;
    } else if (!isWorkOrderSelectValueSet(prev.projectId)) {
        next.projectId = initial.projectId;
    }

    if (!shouldShowSorting(customer)) {
        next.sortingType = null;
    } else if (!isWorkOrderSortingTypeSet(prev.sortingType)) {
        next.sortingType = initial.sortingType;
    }

    if (!shouldShowInvoiceAcceptance(customer)) {
        next.invoiceAcceptanceUserId = null;
    } else if (!isWorkOrderSelectValueSet(prev.invoiceAcceptanceUserId)) {
        next.invoiceAcceptanceUserId = initial.invoiceAcceptanceUserId;
    }

    return next;
};

export type AdministrativeEditValidationError =
    | 'costCategoryRequired'
    | 'chargedDepartmentRequired'
    | 'customerRequired'
    | 'projectRequired'
    | 'sortingRequired'
    | 'responsibleRequired'
    | 'invoiceApproverRequired';

export const validateAdministrativeEditForm = (
    values: AdministrativeFormValues,
    status: number,
    costCategories: CostCategory[],
    customers: Customer[],
): AdministrativeEditValidationError | null => {
    if (!isWorkOrderSelectValueSet(values.costCategoryId)) {
        return 'costCategoryRequired';
    }

    const category = findCostCategory(costCategories, values.costCategoryId);

    if (shouldShowChargedDepartment(category) && !isWorkOrderSelectValueSet(values.chargedDepartmentId)) {
        return 'chargedDepartmentRequired';
    }

    if (shouldShowCustomer(category) && !isWorkOrderSelectValueSet(values.customerId)) {
        return 'customerRequired';
    }

    const customer = findCustomer(customers, values.customerId);

    if (shouldShowProject(customer) && !isWorkOrderSelectValueSet(values.projectId)) {
        return 'projectRequired';
    }

    if (shouldShowSorting(customer) && !isWorkOrderSortingTypeSet(values.sortingType)) {
        return 'sortingRequired';
    }

    if (
        shouldShowCustomer(category)
        && shouldShowInvoiceAcceptance(customer)
        && shouldRequireInvoiceApprover(customer)
        && !isWorkOrderSelectValueSet(values.invoiceAcceptanceUserId)
    ) {
        return 'invoiceApproverRequired';
    }

    if (canEditResponsiblePersonnel(status) && !isWorkOrderSelectValueSet(values.personnelId)) {
        return 'responsibleRequired';
    }

    return null;
};

export const buildAdministrativeSavePayload = (
    detail: WorkOrderDetail,
    values: AdministrativeFormValues,
    changeAll?: boolean,
): SaveAdministrativeDataPayload => ({
    workOrderId: detail.id,
    departmentId: detail.departmentId as number,
    personnelId: values.personnelId,
    repairLocation: normalizeRepairLocation(values.repairLocation),
    workType: values.workType,
    costCategoryId: values.costCategoryId,
    chargedDepartmentId: values.chargedDepartmentId,
    customerId: values.customerId,
    projectId: values.projectId,
    sortingType: values.sortingType,
    invoiceAcceptanceUserId: values.invoiceAcceptanceUserId,
    displayWork: 0,
    ...(changeAll === true || changeAll === false ? { changeAll } : {}),
});
